import { useContext, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import { Box, Container, Grid, Paper, Typography } from "@mui/material";

export default function HelpfaqDesktop() {
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();
  const [role, setRole] = useState("Normal User");

  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setRole(data.role || "Normal User");
      }
    });
    return () => unsub();
  }, [currentUser]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      <LeftMenu />

      <Box sx={{ flex: 1, boxSizing: "border-box", ml: "5px" }}>
        <HeaderBar
          title={translate("HelpCenterTitle") || "Help & Resources"}
          subtitle={
            translate("HelpCenterSubtitle") ||
            "Everything you need to make the most of TalkMate"
          }
          showSearch={false}
        />

        <Box
          sx={{
            px: 4,
            py: 3,
            display: "flex",
            flexDirection: "column",
            minHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.default",
          }}
        >
          {/* Hero */}
          <Container maxWidth="md" sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              {translate("AboutTalkMateTitle") || "What is TalkMate?"}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {translate("AboutTalkMateSubtitle") ||
                "TalkMate helps you deploy AI agents across your website, chat, and support channels, so your users always get fast, intelligent answers."}
            </Typography>
          </Container>

          {/* Plans */}
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              align="center"
              gutterBottom
            >
              {translate("PricingPlansTitle") || "Our Plans"}
            </Typography>
            <Grid container spacing={4} justifyContent="center" sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{ p: 4, borderRadius: 2, bgcolor: "background.paper" }}
                >
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {translate("FreePlanTitle") || "Free Plan"}
                  </Typography>
                  <Typography>
                    •{" "}
                    {translate("FreePlanFeature1") ||
                      "Test an agent with up to 10 questions"}
                  </Typography>
                  <Typography>
                    •{" "}
                    {translate("FreePlanFeature2") ||
                      "Access to essential TalkMate features"}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  sx={{ p: 4, borderRadius: 2, bgcolor: "background.paper" }}
                >
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {translate("PremiumPlanTitle") || "Pay-as-you-grow"}
                  </Typography>
                  <Typography>
                    •{" "}
                    {translate("PremiumPlanFeature1") ||
                      "Pay only for the number of questions asked"}
                  </Typography>
                  <Typography>
                    •{" "}
                    {translate("PremiumPlanFeature2") ||
                      "Unlimited AI agents and channels"}
                  </Typography>
                  <Typography>
                    •{" "}
                    {translate("PremiumPlanFeature3") ||
                      "Email alerts, analytics, and advanced insights"}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>

          {/* Why Choose TalkMate */}
          <Container maxWidth="md" sx={{ py: 6 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              align="center"
              gutterBottom
            >
              {translate("WhyChooseTalkMate") || "Why choose TalkMate?"}
            </Typography>
            <Grid
              container
              spacing={4}
              sx={{ mt: 2, justifyContent: "center" }}
            >
              {[
                {
                  title: "Easy setup",
                  desc: "Install and connect your AI agent in minutes — no coding required.",
                },
                {
                  title: "Multi-channel support",
                  desc: "Deploy your AI on web, chat, and support systems seamlessly.",
                },
                {
                  title: "Scalable pricing",
                  desc: "Pay only for what your customers actually use.",
                },
              ].map((item, i) => (
                <Grid key={i} item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {translate(`WhyChooseFeature${i + 1}Title`) || item.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {translate(`WhyChooseFeature${i + 1}Desc`) || item.desc}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
